'use client';

import { type Table } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { UpsellItem } from '@/lib/api/upsells';
import { BulkDeleteUpsellsModal } from './bulk-delete-upsells-modal';

interface UpsellsTableActionBarProps {
    table: Table<UpsellItem>;
    storeId: string;
}

/**
 * UpsellsTableActionBar component for bulk actions on selected upsells
 */
export const UpsellsTableActionBar = ({ table, storeId }: UpsellsTableActionBarProps) => {
    const { t } = useTranslation('upsells');
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const selectedRows = table.getFilteredSelectedRowModel().rows;

    if (selectedRows.length === 0) {
        return null;
    }

    const selectedCount = selectedRows.length;
    const selectedUpsellIds = selectedRows.map((r) => r.original.id);

    const handleDeleteSuccess = () => {
        table.resetRowSelection();
    };

    return (
        <>
            <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">{selectedCount} {t('actions.selected')}</span>
                <Button variant="outline" size="sm" onClick={() => setShowDeleteModal(true)} className="h-8">
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('actions.deleteSelected')}
                </Button>
            </div>

            {showDeleteModal && (
                <BulkDeleteUpsellsModal
                    upsellIds={selectedUpsellIds}
                    storeId={storeId}
                    onClose={() => setShowDeleteModal(false)}
                    onSuccess={handleDeleteSuccess}
                />
            )}
        </>
    );
};
